import { useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Form, Formik } from "formik";
import { Button } from "app/components/ui/Button/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FilterSortPopover } from "app/features/product/components/filterSortPopover/FilterSortPopover";
import { AnimatedWidthButton } from "app/features/product/components/animatedWidthButton/AnimatedWidthButton";
import FilterIcon from "app/features/product/productFilters/icons/FilterIcon";
import { Drawer } from "app/components/ui/drawer/Drawer";
import { FilterChip } from "app/features/product/productFilters/components/mobile/FilterChip";
import {
	FilterCollapsible,
	FilterCollapsiblePanel,
} from "app/features/product/productFilters/components/mobile/FilterCollapsible";
import Separator from "app/pages/.shared/Separator";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import classNames from "classnames";
import QuotationBoardFilterOptions from "./QuotationBoardFilterOptions";
import "./QuotationBoardFilter.scss";

/**
 * Board filter component for the quotation page.
 * Displays a popover on desktop and a drawer on mobile.
 */
const QuotationBoardFilter = ({
	hideDiscountHBX = false,
	availableBoards = [],
	selectedBoardCodes = [],
	onFilterChange,
	onReset,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const { track } = useAnalytics();

	const [isOpen, setIsOpen] = useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const selectedCount = selectedBoardCodes.length;
	const hasFilters = selectedCount > 0;

	const initialValues = useMemo(
		() => ({
			boards: selectedBoardCodes,
		}),
		[selectedBoardCodes]
	);

	const handleSubmit = useCallback(
		values => {
			const discountedBoards = availableBoards
				.filter(board => board.isDiscounted)
				.map(board => board.boardCode);

			const userSelectedDiscountedBoards = discountedBoards.some(board =>
				values.boards.includes(board)
			);

			track("quote_apply_board-filter", {
				boards: values.boards,
				isDiscounted: userSelectedDiscountedBoards,
			});

			if (onFilterChange) {
				onFilterChange(values.boards);
			}
			if (isMobile) {
				setIsDrawerOpen(false);
			} else {
				setIsOpen(false);
			}
		},
		[onFilterChange, isMobile, track]
	);

	const handleFilterClick = useCallback(() => {
		track("quote_click_board-filter");
	}, [track]);

	if (!availableBoards || availableBoards.length === 0) {
		return null;
	}

	// Mobile version with Drawer
	if (isMobile) {
		return (
			<div className="quotation-board-filter">
				<Drawer.Root open={isDrawerOpen} onOpenChange={setIsDrawerOpen} side="right">
					<Drawer.Trigger>
						<Button
							startIcon={<FilterIcon />}
							className={classNames("quotation-board-filter__trigger", {
								"quotation-board-filter__trigger--active": hasFilters,
							})}
							onClick={handleFilterClick}
							data-testid="quotation-board-filter-trigger-mobile"
						>
							<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
								<FormattedMessage id="quotation.board.filter.label" />
								{hasFilters && ` (${selectedCount})`}
							</Typography>
						</Button>
					</Drawer.Trigger>

					<Drawer.Content>
						<Formik
							initialValues={initialValues}
							onSubmit={handleSubmit}
							enableReinitialize={true}
						>
							{({ values, setFieldValue }) => {
								const selectedOptions = availableBoards.filter(board =>
									values.boards?.includes(board.boardCode)
								);
								const hasSelectedOptions = selectedOptions.length > 0;

								const handleRemoveChip = boardCode => {
									const newBoards = values.boards.filter(
										code => code !== boardCode
									);
									setFieldValue("boards", newBoards);
								};

								return (
									<Form className="quotation-board-filter__drawer-form">
										<Drawer.Header>
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												component="h2"
												isBold
												className="quotation-board-filter__drawer-title"
											>
												<FormattedMessage id="quotation.board.filter.label" />
											</Typography>

											{hasSelectedOptions && (
												<Button
													variant="borderless"
													onClick={() => setFieldValue("boards", [])}
													className="quotation-board-filter__reset-all-button"
												>
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														component="span"
														isBold
													>
														<FormattedMessage id="general.filter.raz.label" />
													</Typography>
												</Button>
											)}
										</Drawer.Header>

										<Drawer.Body className="quotation-board-filter__drawer-body">
											{hasSelectedOptions && (
												<>
													<ul className="quotation-board-filter__selected-filters">
														{selectedOptions.map(board => (
															<li key={board.boardCode}>
																<FilterChip
																	label={board.label}
																	onRemove={() =>
																		handleRemoveChip(
																			board.boardCode
																		)
																	}
																/>
															</li>
														))}
													</ul>
													<Separator design="full" />
												</>
											)}

											<FilterCollapsible
												accordion={true}
												defaultActiveKey="boards"
											>
												<FilterCollapsiblePanel
													key="boards"
													title={
														<Typography
															variant={TYPOGRAPHY_VARIANTS.LARGE}
															component="h3"
															isBold
														>
															<FormattedMessage id="product.page.filter.boards.label" />
															{hasSelectedOptions &&
																` (${selectedOptions.length})`}
														</Typography>
													}
												>
													<QuotationBoardFilterOptions
														availableBoards={availableBoards}
														hideDiscountHBX={hideDiscountHBX}
													/>
												</FilterCollapsiblePanel>
											</FilterCollapsible>
										</Drawer.Body>

										<Drawer.Footer className="quotation-board-filter__drawer-footer">
											<Button
												size="lg"
												type="submit"
												className="quotation-board-filter__drawer-submit"
												fullWidth
											>
												<Typography
													variant={TYPOGRAPHY_VARIANTS.LARGE}
													component="span"
													isBold
												>
													<FormattedMessage id="sdp.filter.apply.button.label" />
												</Typography>
											</Button>
										</Drawer.Footer>
									</Form>
								);
							}}
						</Formik>
					</Drawer.Content>
				</Drawer.Root>
			</div>
		);
	}

	// Desktop version with popover
	return (
		<div className="quotation-board-filter">
			<Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
				{({ values, resetForm }) => (
					<FilterSortPopover.Root
						id="quotation-board-filter"
						open={isOpen}
						onOpen={() => {
							handleFilterClick();
							setIsOpen(true);
						}}
						onClose={() => {
							setIsOpen(false);
							resetForm({ values: { boards: selectedBoardCodes } });
						}}
						placement="bottom-start"
					>
						<FilterSortPopover.Trigger>
							<AnimatedWidthButton
								isActive={hasFilters || isOpen}
								isOpen={isOpen}
								data-testid="quotation-board-filter-trigger"
							>
								<FilterIcon size={16} />
								<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} component="span">
									<FormattedMessage id="quotation.board.filter.label" />
									{hasFilters && ` (${selectedCount})`}
								</Typography>
							</AnimatedWidthButton>
						</FilterSortPopover.Trigger>

						<FilterSortPopover.Content className="quotation-board-filter__popover">
							<Form className="quotation-board-filter__form">
								<QuotationBoardFilterOptions
									availableBoards={availableBoards}
									hideDiscountHBX={hideDiscountHBX}
								/>

								<footer className="quotation-board-filter__actions">
									<Button
										size="sm"
										className="quotation-board-filter__apply-button"
										type="submit"
									>
										<FormattedMessage id="sdp.filter.apply.button.label" />
									</Button>

									<Button
										variant="borderless"
										size="sm"
										onClick={() => resetForm({ values: { boards: [] } })}
										className={classNames(
											"quotation-board-filter__reset-button",
											{
												"quotation-board-filter__reset-button--visible":
													values.boards?.length > 0,
											}
										)}
										aria-hidden={values.boards?.length === 0}
										tabIndex={values.boards?.length > 0 ? 0 : -1}
									>
										<Typography
											variant={TYPOGRAPHY_VARIANTS.SMALL}
											isBold="true"
										>
											<FormattedMessage id="sdp.filter.reset.button.label" />
										</Typography>
									</Button>
								</footer>
							</Form>
						</FilterSortPopover.Content>
					</FilterSortPopover.Root>
				)}
			</Formik>

			{hasFilters && (
				<Button
					variant="borderless"
					className="quotation-board-filter__clear"
					onClick={onReset}
					data-testid="quotation-board-filter-clear"
				>
					<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold="true">
						<FormattedMessage id="sdp.filter.clear.button.label" />
					</Typography>
				</Button>
			)}
		</div>
	);
};

QuotationBoardFilter.propTypes = {
	availableBoards: PropTypes.arrayOf(
		PropTypes.shape({
			boardCode: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		})
	),
	selectedBoardCodes: PropTypes.arrayOf(PropTypes.string),
	onFilterChange: PropTypes.func,
	onReset: PropTypes.func,
	hideDiscountHBX: PropTypes.bool,
};

export default QuotationBoardFilter;
